/**
 * Базовые типы и интерфейсы для игровой системы OwlCat Club
 */

// Константы по умолчанию
export const DEFAULT_GAME_SETTINGS: BaseGameSettings = {
  difficulty: 'medium',
  soundEnabled: true,
  hintsEnabled: true,
  timerEnabled: true,
  autoSave: true,
};

const GAME_DIFFICULTIES = {
  easy: {
    name: 'Easy',
    description: 'Perfect for beginners',
    multiplier: 0.5,
  },
  medium: {
    name: 'Medium', 
    description: 'Balanced challenge',
    multiplier: 1.0,
  },
  hard: {
    name: 'Hard',
    description: 'For experienced players',
    multiplier: 1.5,
  },
  expert: {
    name: 'Expert',
    description: 'Ultimate challenge',
    multiplier: 2.0,
  },
} as const;

// Статус игры
export type GameStatus = 'idle' | 'playing' | 'paused' | 'completed' | 'failed';

// Уровни сложности
export type GameDifficulty = 'easy' | 'medium' | 'hard' | 'expert';

// Базовые настройки игры
export interface BaseGameSettings {
  difficulty: GameDifficulty;
  soundEnabled: boolean;
  hintsEnabled: boolean;
  timerEnabled: boolean;
  autoSave: boolean;
}

// Статистика игры
export interface GameStatistics {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  totalTime: number;
  bestTime?: number;
  averageTime?: number;
  hintsUsed: number;
  currentStreak: number;
  bestStreak: number;
}

// Базовый интерфейс игры
export interface BaseGame {
  id: string;
  name: string;
  description: string;
  version: string;
  status: GameStatus;
  difficulty: GameDifficulty;
  settings: BaseGameSettings;
  statistics: GameStatistics;
  createdAt: Date;
  updatedAt: Date;
  startedAt?: Date;
  completedAt?: Date;
}

// Результат игры
export interface GameResult {
  gameId: string;
  status: 'won' | 'lost' | 'abandoned';
  score?: number;
  time: number;
  difficulty: GameDifficulty;
  hintsUsed: number;
  completedAt: Date;
  metadata?: Record<string, any>;
}

// Хук состояния игры
export interface UseGameStateReturn<T extends BaseGame = BaseGame> {
  game: T;
  isLoading: boolean;
  error: string | null;
  updateGame: (updates: Partial<T>) => void;
  resetGame: () => void;
  startGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  completeGame: (result?: Partial<GameResult>) => void;
  failGame: (reason?: string) => void;
}

// Хук таймера игры
export interface UseGameTimerReturn {
  elapsedTime: number;
  formattedTime: string;
  isRunning: boolean;
  isPaused: boolean;
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  reset: () => void;
}

// Хук полноэкранного режима
export interface UseFullscreenReturn {
  isFullscreen: boolean;
  isSupported: boolean;
  toggle: () => Promise<void>;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  element: HTMLElement | null;
}

// Хук настроек игры
export interface UseGameSettingsReturn<T extends BaseGameSettings = BaseGameSettings> {
  settings: T;
  isLoading: boolean;
  updateSettings: (updates: Partial<T>) => void;
  resetSettings: () => void;
  saveSettings: () => Promise<void>;
  loadSettings: () => Promise<void>;
}

export const DEFAULT_GAME_STATISTICS: GameStatistics = {
  gamesPlayed: 0,
  gamesWon: 0,
  gamesLost: 0,
  totalTime: 0,
  hintsUsed: 0,
  currentStreak: 0,
  bestStreak: 0,
};
